const STORAGE_KEY = 'workbench-history-badge-v1';
const WORKSPACE_MODE_KEY = 'ipe_workspace_mode';
const MAX_VIEWED_PER_SCOPE = 500;
const MODE_SUFFIX_RE = /:(personal|team)$/;
const listeners = new Set();

function readCurrentWorkspaceMode() {
  if (typeof localStorage === 'undefined') return 'personal';
  try {
    const mode = String(localStorage.getItem(WORKSPACE_MODE_KEY) || 'personal').trim().toLowerCase();
    return mode === 'team' ? 'team' : 'personal';
  } catch {
    return 'personal';
  }
}

/** @returns {'personal' | 'team'} */
export function normalizeWorkspaceMode(mode) {
  return String(mode || '').trim().toLowerCase() === 'team' ? 'team' : 'personal';
}

/**
 * 功能 scope + workspaceMode → 存储 key（如 img2img:personal）。
 * 若 scopeId 已带 :personal/:team，可被显式 workspaceMode 覆盖。
 */
export function buildHistoryBadgeScopeKey(scopeId, workspaceMode) {
  const raw = String(scopeId || '').trim();
  if (!raw) return '';
  const matched = raw.match(MODE_SUFFIX_RE);
  const base = matched ? raw.slice(0, -matched[0].length) : raw;
  if (!base) return '';
  const hasExplicitMode = workspaceMode != null && String(workspaceMode).trim() !== '';
  const mode = hasExplicitMode
    ? normalizeWorkspaceMode(workspaceMode)
    : (matched ? matched[1] : readCurrentWorkspaceMode());
  return `${base}:${mode}`;
}

export function parseHistoryBadgeScopeKey(scopeKey) {
  const raw = String(scopeKey || '').trim();
  const matched = raw.match(MODE_SUFFIX_RE);
  if (!matched) {
    return { scopeId: raw, workspaceMode: null };
  }
  return {
    scopeId: raw.slice(0, -matched[0].length),
    workspaceMode: matched[1],
  };
}

function mergeScopeStates(a, b) {
  const pending = [
    ...new Set(
      [...(Array.isArray(a?.pending) ? a.pending : []), ...(Array.isArray(b?.pending) ? b.pending : [])]
        .map(String)
        .filter(Boolean),
    ),
  ];
  const viewed = [
    ...new Set(
      [...(Array.isArray(a?.viewed) ? a.viewed : []), ...(Array.isArray(b?.viewed) ? b.viewed : [])]
        .map(String)
        .filter(Boolean),
    ),
  ];
  return {
    pending: pending.filter((id) => !viewed.includes(id)),
    viewed,
  };
}

/** 旧无 mode 的桶归入 personal，避免串读 / 串清 */
export function migrateLegacyHistoryBadgeStore(store) {
  if (!store || typeof store !== 'object') return false;
  let changed = false;
  Object.keys(store).forEach((key) => {
    if (MODE_SUFFIX_RE.test(key)) return;
    const legacy = normalizeScopeState(store[key]);
    const personalKey = `${key}:personal`;
    store[personalKey] = mergeScopeStates(store[personalKey], legacy);
    delete store[key];
    changed = true;
  });
  return changed;
}

function loadStore() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    const store = parsed && typeof parsed === 'object' ? parsed : {};
    if (migrateLegacyHistoryBadgeStore(store)) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    }
    return store;
  } catch {
    return {};
  }
}

function normalizeScopeState(raw) {
  const pending = Array.isArray(raw?.pending)
    ? raw.pending.map(String).filter(Boolean)
    : [];
  const viewed = Array.isArray(raw?.viewed)
    ? raw.viewed.map(String).filter(Boolean)
    : [];
  const pendingSet = new Set(pending);
  return {
    pending: [...pendingSet],
    viewed: [...new Set(viewed)],
  };
}

function getScopeState(store, scopeKey) {
  const key = String(scopeKey || '').trim();
  if (!key) return { pending: [], viewed: [] };
  if (!store[key]) {
    store[key] = { pending: [], viewed: [] };
  }
  store[key] = normalizeScopeState(store[key]);
  return store[key];
}

function saveStore(store) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  listeners.forEach((fn) => {
    try {
      fn(store);
    } catch (e) {
      console.warn('[历史记录角标] 通知订阅失败:', e);
    }
  });
}

function mutateScope(scopeId, workspaceMode, mutator) {
  const scopeKey = buildHistoryBadgeScopeKey(scopeId, workspaceMode);
  if (!scopeKey) return;
  const store = loadStore();
  const state = getScopeState(store, scopeKey);
  mutator(state);
  state.pending = [...new Set(state.pending.filter((id) => !state.viewed.includes(id)))];
  if (state.viewed.length > MAX_VIEWED_PER_SCOPE) {
    state.viewed = state.viewed.slice(-MAX_VIEWED_PER_SCOPE);
  }
  store[scopeKey] = state;
  saveStore(store);
}

export function subscribeHistoryBadge(listener) {
  if (typeof listener !== 'function') return () => {};
  listener(loadStore());
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getPendingCount(scopeId, workspaceMode) {
  const scopeKey = buildHistoryBadgeScopeKey(scopeId, workspaceMode);
  if (!scopeKey) return 0;
  const store = loadStore();
  return getScopeState(store, scopeKey).pending.length;
}

export function trackPendingTask(scopeId, taskId, workspaceMode) {
  const id = String(taskId || '').trim();
  if (!id) return;
  mutateScope(scopeId, workspaceMode, (state) => {
    if (state.viewed.includes(id)) return;
    if (!state.pending.includes(id)) {
      state.pending.push(id);
    }
  });
}

export function markTaskViewedFromHistory(scopeId, taskId, workspaceMode) {
  const id = String(taskId || '').trim();
  if (!id) return;
  mutateScope(scopeId, workspaceMode, (state) => {
    state.pending = state.pending.filter((item) => item !== id);
    if (!state.viewed.includes(id)) {
      state.viewed.push(id);
    }
  });
}

export function syncDiscoveredInProgressTasks(scopeId, taskIds, workspaceMode) {
  const ids = (taskIds || []).map(String).filter(Boolean);
  if (ids.length === 0) return;
  mutateScope(scopeId, workspaceMode, (state) => {
    ids.forEach((id) => {
      if (state.viewed.includes(id)) return;
      if (!state.pending.includes(id)) {
        state.pending.push(id);
      }
    });
  });
}

export function removePendingTasks(scopeId, taskIds, workspaceMode) {
  const ids = new Set((taskIds || []).map(String).filter(Boolean));
  if (ids.size === 0) return;
  mutateScope(scopeId, workspaceMode, (state) => {
    state.pending = state.pending.filter((id) => !ids.has(id));
    state.viewed = state.viewed.filter((id) => !ids.has(id));
  });
}

/** 列表为空时清空该 scope 下全部未读角标（避免本地残留 pending） */
export function clearScopePending(scopeId, workspaceMode) {
  mutateScope(scopeId, workspaceMode, (state) => {
    state.pending = [];
  });
}

/**
 * 历史面板拉取服务端列表后对齐角标：
 * - 总数为 0 → 清空 pending
 * - 有数据 → 同步进行中任务到 pending
 */
export function reconcileHistoryBadgeWithServerTasks(
  scopeId,
  tasks,
  workspaceMode,
  options = {},
) {
  const rows = Array.isArray(tasks) ? tasks : [];
  const total = Number.isFinite(options.total)
    ? Number(options.total)
    : rows.length;

  if (total <= 0 && rows.length === 0) {
    clearScopePending(scopeId, workspaceMode);
    return;
  }

  const resolveTaskId = typeof options.resolveTaskId === 'function'
    ? options.resolveTaskId
    : (task) => task?.id;
  const inProgressStatuses = new Set(
    (options.inProgressStatuses || [
      'waiting', 'executing', 'processing', 'pending', 'new', 'running', 'queued',
    ]).map((s) => String(s).toLowerCase()),
  );

  const inProgressIds = rows
    .filter((task) => inProgressStatuses.has(String(task?.status || '').toLowerCase()))
    .map((task) => String(resolveTaskId(task) || '').trim())
    .filter(Boolean);

  if (inProgressIds.length > 0) {
    syncDiscoveredInProgressTasks(scopeId, inProgressIds, workspaceMode);
  }
}
